import { useMemo } from 'react'
import { where } from 'firebase/firestore'
import { useCollection } from './firestore'
import { useTours } from './tours'
import { chunkIds } from './firestore'
import type { Contact, Hotel, Show, Venue, WithId } from '../types/models'

/* ============================================================================
   The address book.

   Venues are the awkward one. `DBAddVenueView` writes to a `venues`
   collection, but the Mac's Venues list is built from the *shows* on your
   tours and never reads that collection — so a venue you saved by hand never
   appeared, and a venue you played was never editable.

   This merges both: saved venues are the record, played venues fill in the
   ones you never got round to saving, and anything matching by name and city
   collapses into one row that knows how many times you've played it.
   ============================================================================ */

export interface VenueEntry {
  /** `venues` document id when saved; otherwise a synthetic key. */
  id: string
  saved: boolean
  name: string
  city: string
  country?: string | null
  address?: string | null
  capacity?: number | null
  contactName?: string | null
  contactEmail?: string | null
  contactPhone?: string | null
  /** How many shows across your tours have been played here. */
  playCount: number
  /** The most recent show at this venue, if any. */
  lastPlayed?: Date | null
}

function venueKey(name: string, city: string): string {
  return `${name.trim().toLowerCase()}|${city.trim().toLowerCase()}`
}

export function useVenues(userId: string | null | undefined) {
  const { tours } = useTours(userId)
  const saved = useCollection<Venue>(
    'venues',
    userId ? [where('ownerId', '==', userId)] : null,
  )

  // Shows carry the venue, so the played history comes from there. `in` caps
  // at 30, which is also plenty — nobody has 30 concurrent tours.
  const tourIds = useMemo(() => tours.map((tour) => tour.id), [tours])
  const shows = useCollection<Show>(
    'shows',
    tourIds.length > 0 ? [where('tourId', 'in', chunkIds(tourIds, 30)[0])] : null,
  )

  const venues = useMemo(() => {
    const byKey = new Map<string, VenueEntry>()

    for (const venue of saved.data) {
      byKey.set(venueKey(venue.name, venue.city), {
        id: venue.id,
        saved: true,
        name: venue.name,
        city: venue.city,
        country: venue.country,
        address: venue.address,
        capacity: venue.capacity,
        contactName: venue.contactName,
        contactEmail: venue.contactEmail,
        contactPhone: venue.contactPhone,
        playCount: 0,
        lastPlayed: null,
      })
    }

    for (const show of shows.data) {
      if (!show.venueName) continue
      const key = venueKey(show.venueName, show.city ?? '')
      const played = show.date.toDate()
      const existing = byKey.get(key)

      if (existing) {
        existing.playCount += 1
        if (!existing.lastPlayed || played > existing.lastPlayed) existing.lastPlayed = played
        // A saved venue keeps its own detail; the show only ever fills gaps.
        existing.address ||= show.venueAddress
        existing.country ||= show.country
        existing.contactName ||= show.contactName
        existing.contactEmail ||= show.contactEmail
        existing.contactPhone ||= show.contactPhone
        continue
      }

      byKey.set(key, {
        id: `show:${key}`,
        saved: false,
        name: show.venueName,
        city: show.city ?? '',
        country: show.country,
        address: show.venueAddress,
        capacity: null,
        contactName: show.contactName,
        contactEmail: show.contactEmail,
        contactPhone: show.contactPhone,
        playCount: 1,
        lastPlayed: played,
      })
    }

    return Array.from(byKey.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [saved.data, shows.data])

  return { venues, loading: saved.loading || shows.loading }
}

/* ------------------------------------------------------------------ people */

export function useContacts(userId: string | null | undefined) {
  const contacts = useCollection<Contact>(
    'contacts',
    userId ? [where('ownerId', '==', userId)] : null,
  )

  const sorted = useMemo(
    () => [...contacts.data].sort((a, b) => a.name.localeCompare(b.name)),
    [contacts.data],
  )

  /** Every role in use, for the filter chips. */
  const roles = useMemo(() => {
    const set = new Set<string>()
    for (const contact of contacts.data) {
      for (const role of contact.roles ?? []) if (role.trim()) set.add(role.trim())
    }
    return Array.from(set).sort()
  }, [contacts.data])

  return { contacts: sorted, roles, loading: contacts.loading }
}

/* ------------------------------------------------------------------ hotels */

export function useSavedHotels(userId: string | null | undefined) {
  const hotels = useCollection<Hotel>(
    'hotels',
    userId ? [where('ownerId', '==', userId)] : null,
  )

  const sorted = useMemo(
    () =>
      [...hotels.data].sort(
        (a, b) => b.checkInDate.toMillis() - a.checkInDate.toMillis(),
      ),
    [hotels.data],
  )

  return { hotels: sorted, loading: hotels.loading }
}

/* ------------------------------------------------------------------ search */

/** Case-insensitive match across a record's searchable text. */
export function matches(query: string, ...fields: Array<string | null | undefined>): boolean {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  return fields.some((field) => (field ?? '').toLowerCase().includes(needle))
}

export type { Contact, Hotel, Venue, WithId }
